import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  OnChanges,
  SimpleChanges,
  ViewChild,
  AfterViewInit,
  OnInit,
  OnDestroy,
} from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { MatSelect, MatSelectChange } from '@angular/material/select';
import { Subscription } from 'rxjs';
import { validationMessages } from '../../messages';
import { IdName } from '../../models/id-name';
import { IOptionGroup } from '../../models/option-group.model';
import { MatCheckboxChange } from '@angular/material/checkbox';

@Component({
  selector: 'app-ui-select-group',
  templateUrl: './ui-select-group.component.html',
  styleUrls: ['./ui-select-group.component.scss'],
  standalone: false,
})
export class UiSelectGroupComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
  @Input() label = '';
  @Input() control: UntypedFormControl;
  @Input() optionGroups: IOptionGroup[] | null;
  @Input() messages = validationMessages;
  @Input() selected: string[] = [];
  @Input() isMultiple = true;
  @Input() disabled = false;
  @Input() description = '';
  @Input() required = false;
  @Input() returnWholeObject = false;
  @Input() touched = false;
  @Input() selectAll = false;
  @Input() enableSearch = true;

  areSomeRowsSelected = false;
  areAllRowsSelected = false;

  @ViewChild('searchInput') searchInput: ElementRef;

  filteredOptionGroups: IOptionGroup[] | null = null;
  private initialOptionGroups: IOptionGroup[] | null = null;
  private searchTerm = '';

  displayedOptionGroups: IOptionGroup[] = [];

  @Output() readonly changeHandler: EventEmitter<MatSelectChange> =
    new EventEmitter<MatSelectChange>();

  private subscription = new Subscription();

  ngOnInit(): void {
    if (this.control) {
      this.subscription.add(
        this.control.valueChanges.subscribe(() => {
          this.updateDisplayedOptionGroups();
          if (this.selectAll) {
            this.setSelectAllCheckboxProperties();
          }
        })
      );
      this.updateDisplayedOptionGroups();
    }
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.touched?.currentValue === true) {
      this.control.markAllAsTouched();
    }
    if (changes.optionGroups?.currentValue) {
      this.initialOptionGroups = changes.optionGroups.currentValue;
      this.applySearchFilter();
    }
    if (this.selectAll) {
      this.setSelectAllCheckboxProperties();
    }
  }

  @ViewChild(MatSelect)
  private select: any;

  ngAfterViewInit() {
    // dirty hack to prevent random scroll jumps when selecting an option from any group other than the first one. Custom mat-option height isn't supported since 2017
    // https://github.com/angular/components/blob/main/src/material/select/select.ts#L263
    if (this.select) {
      this.select._scrollOptionIntoView = () => {
        return;
      };
    }
  }

  onPanelOpenedChange(opened: boolean): void {
    if (opened) {
      this.scrollPanelToTop();
      this.focusSearchInput();
    } else {
      this.clearSearchOnClose();
    }
  }

  private scrollPanelToTop(): void {
    setTimeout(() => {
      const panel = document.querySelector('.mat-mdc-select-panel.app-ui-select');
      if (panel) {
        (panel as HTMLElement).scrollTop = 0;
      }
    }, 50);
  }

  onSearch(event: KeyboardEvent): void {
    const target = event.target as HTMLInputElement;
    const value = target?.value ?? '';

    if (
      event.code === 'Space' ||
      event.key === ' ' ||
      event.key === 'Spacebar' ||
      event.key === 'Space'
    ) {
      event.preventDefault();
      event.stopPropagation();
      target.value += ' ';
    }

    this.searchTerm = value;
    this.applySearchFilter();
  }

  private applySearchFilter(): void {
    const groups = this.initialOptionGroups ?? this.optionGroups ?? [];
    if (!this.searchTerm.trim()) {
      this.filteredOptionGroups = groups;
    } else {
      const filter = this.searchTerm.toLowerCase();
      this.filteredOptionGroups = groups
        .map((group) => ({
          ...group,
          options: group.options.filter((opt) => (opt.name ?? '').toLowerCase().includes(filter)),
        }))
        .filter((group) => group.options.length > 0);
    }
    this.updateDisplayedOptionGroups();
  }

  private clearSearchOnClose(): void {
    this.searchTerm = '';
    this.applySearchFilter();
    if (this.searchInput?.nativeElement) {
      this.searchInput.nativeElement.value = '';
    }
  }

  private focusSearchInput(): void {
    if (this.searchInput?.nativeElement) {
      this.searchInput.nativeElement.value = '';
      this.searchInput.nativeElement.focus();
    }
    this.applySearchFilter();
  }

  private updateDisplayedOptionGroups(): void {
    const groups = this.filteredOptionGroups ?? this.optionGroups ?? [];
    const selectedValues = Array.isArray(this.control?.value) ? this.control.value : [];
    const allGroups = this.initialOptionGroups ?? this.optionGroups ?? [];
    if (selectedValues.length === 0) {
      this.displayedOptionGroups = groups;
      return;
    }
    const groupTypesInFiltered = new Set(groups.map((g) => g.type));
    const result = groups.map((group) => {
      const filteredOptions = group.options;
      const filteredIds = new Set(filteredOptions.map((o) => o.id));
      const selectedInGroup = this.getSelectedOptionsForGroup(
        selectedValues,
        group.type,
        allGroups
      ).filter((opt) => !filteredIds.has(opt.id));
      return {
        ...group,
        options: [...filteredOptions, ...selectedInGroup],
      };
    });
    const groupTypesInResult = new Set(result.map((g) => g.type));
    allGroups.forEach((group) => {
      if (!groupTypesInResult.has(group.type)) {
        const selectedInGroup = this.getSelectedOptionsForGroup(
          selectedValues,
          group.type,
          allGroups
        );
        if (selectedInGroup.length > 0) {
          result.push({ ...group, options: selectedInGroup });
        }
      }
    });
    this.displayedOptionGroups = result;
  }

  private getSelectedOptionsForGroup(
    selectedValues: unknown[],
    groupType: string,
    allGroups: IOptionGroup[]
  ): IdName[] {
    if (this.returnWholeObject) {
      return selectedValues
        .filter((sv: any) => sv?.type === groupType && sv?.value?.id)
        .map((sv: any) => sv.value);
    }
    const group = allGroups.find((g) => g.type === groupType);
    const groupOptionIds = new Set(group?.options?.map((o) => o.id) ?? []);
    return selectedValues
      .filter((id) => groupOptionIds.has(id as string))
      .map((id) => group!.options.find((o) => o.id === id)!)
      .filter(Boolean);
  }

  onChange(event: MatSelectChange): void {
    this.changeHandler.emit(event);
  }

  compareWith(o1: any, o2: any): boolean {
    return o1.value?.id && o2.value?.id ? o1.value?.id === o2.value?.id : Object.is(o1, o2);
  }

  private getTotalOptionsCount(): number {
    return this.displayedOptionGroups.reduce((sum, group) => sum + (group.options?.length || 0), 0);
  }

  setSelectAllCheckboxProperties(): void {
    const totalOptions = this.getTotalOptionsCount();
    const groups = this.displayedOptionGroups;
    if (groups.length > 0 && Array.isArray(this.control.value)) {
      this.areSomeRowsSelected =
        this.control.value.length > 0 && this.control.value.length < totalOptions;
      this.areAllRowsSelected = totalOptions > 0 && this.control.value.length === totalOptions;
    } else {
      this.areAllRowsSelected = false;
      this.areSomeRowsSelected = false;
    }
  }

  toggleSelection(change: MatCheckboxChange): void {
    if (change.checked) {
      const groups = this.displayedOptionGroups;
      const allOptions = groups.flatMap((group) =>
        group.options.map((option) =>
          this.returnWholeObject ? { value: option, type: group.type } : option.id
        )
      );
      this.control.setValue(allOptions || []);
    } else {
      this.control.setValue([]);
    }
    this.setSelectAllCheckboxProperties();
  }
}
